import { Lightning } from "@lightningjs/sdk";
import { PlayerProgress } from "./components/PlayerProgress";
import { PlayPauseButton } from "./components/PlayPauseButton";
import { SettingButton } from "./components/SettingButton";
import theme from "../../../../lib/theme";

interface PlayerBottomRowTemplateSpec extends Lightning.Component.TemplateSpec {
    Progress: typeof PlayerProgress;
    ControlsRow: {
        PlayPause: typeof PlayPauseButton;
        SettingIcon: typeof SettingButton;
    };
    duration: number;
    currentTime: number;
}

export class PlayerBottomRow
    extends Lightning.Component<PlayerBottomRowTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PlayerBottomRowTemplateSpec>
{
    _duration = 0;
    private _hasSettings = false; // true means settings ARE available, false means NOT available

    readonly Progress = this.getByRef("Progress")!;
    readonly ControlsRow = this.getByRef("ControlsRow")!;

    get PlayPause() {
        return this.ControlsRow.getByRef("PlayPause")!;
    }

    get SettingIcon() {
        return this.ControlsRow.getByRef("SettingIcon")!;
    }

    setHasSettings(hasSettings: boolean) {
        this._hasSettings = hasSettings; // true means settings ARE available
    }

    $setFocusToSettingIcon() {
        if (this._hasSettings) {
            this._setState("SettingState");
        }
    }

    static override _template(): Lightning.Component.Template<PlayerBottomRowTemplateSpec> {
        return {
            w: theme.layout.w - 72 * 2,
            x: 72,
            y: -42,
            flex: { direction: "column", alignItems: "stretch" },
            Progress: {
                width: theme.layout.w - 72 * 2,
                h: 8,
                type: PlayerProgress,
                showPin: true,
                flexItem: {
                    marginBottom: 10
                }
            },
            ControlsRow: {
                collision: true,
                y: 20,
                w: theme.layout.w - 72 * 2,
                flex: { direction: "row", alignItems: "center", justifyContent: "space-between" },
                PlayPause: {
                    type: PlayPauseButton,
                    signals: {
                        handleHover: "setPlayPauseState"
                    }
                },
                SettingIcon: {
                    w: theme.playback.icon.size,
                    h: theme.playback.icon.size,
                    alpha: 0,
                    type: SettingButton,
                    signals: {
                        handleClick: "$showSettingsPopup",
                        setPlaybackSpeed: "$setPlaybackSpeed",
                        setFocusToSettingIcon: "$setFocusToSettingIcon"
                    }
                }
            }
        };
    }

    static override _states() {
        return [
            class ProgressState extends this {},
            class PlayPauseState extends this {
                override _getFocused() {
                    return this.PlayPause as any;
                }

                override _handleUp() {
                    this._setState("");
                }

                override _handleRight() {
                    if (this._hasSettings) {
                        this._setState("SettingState");
                    }
                }

                override _handleLeft() {}
            },
            class SettingState extends this {
                override _getFocused() {
                    return this.SettingIcon as any;
                }

                $setPlaybackSpeed(value: number) {
                    this.fireAncestors("$setPlaybackSpeed", value);
                }

                $showSettingsPopup() {
                    this.fireAncestors("$showSettingsPopup");
                }

                override _handleEnter() {
                    this.fireAncestors("$showSettingsPopup");
                }

                override _handleUp() {
                    this._setState("");
                }

                override _handleLeft() {
                    this._setState("PlayPauseState");
                }

                override _handleRight() {}
            }
        ];
    }

    override _handleRight() {
        this.fireAncestors("$triggerVideoForward");
    }

    override _handleLeft() {
        this.fireAncestors("$triggerVideoRewind");
    }

    override _handleDown() {
        this._setState("PlayPauseState");
    }

    override _getFocused() {
        return this.Progress;
    }

    override _active() {
        if (window.selectedMovie?.__typename === "LiveStream") {
            this.PlayPause.patch({
                alpha: 0
            });
            this.SettingIcon.patch({
                alpha: 0
            });
        }
    }

    getSettingIcon() {
        return this.SettingIcon;
    }

    hideSettingIcon() {
        this.SettingIcon.patch({
            alpha: 0
        });
    }

    showSettingIcon() {
        this.SettingIcon.patch({
            alpha: 1
        });
    }

    triggeredPlayPause() {
        this.PlayPause.triggeredPlayPause();
    }

    triggeredPause() {
        this.PlayPause.triggeredPause();
    }

    triggeredPlay() {
        this.PlayPause.triggeredPlay();
    }

    setPlayState(value: boolean) {
        this.PlayPause.setPlayState(value);
    }

    setRewindState() {
        this._setState("");
    }

    setPlayPauseState() {
        this._setState("PlayPauseState");
    }

    setForwardState() {
        this._setState("");
    }

    override _handleEnter(): boolean | void {
        return false;
    }

    set duration(value: number) {
        console.log("SET DURATION", value);
        this._duration = value;
    }

    set currentTime(value: number) {
        if (this._duration) {
            this.Progress.patch({
                progress: value ? value / this._duration : 0
            });
        }
    }
}
